import React, { useEffect, useMemo, useState } from 'react';
import { unstable_trace as trace } from "scheduler/tracing";
import Message from 'Components/Message/Message';
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import styles from 'assets/css/MessageCenter.module.css';
import LoadingHandler from 'Components/LoadingWrapper';
import { IconAngleDownBold }  from '@ace/react-icons';
import { useQueryClient } from 'react-query';
import { useEmitterListener, USE_RETRIEVE_DATA_EVENTS, fetchHelper } from '@ace/react-utilities';
import * as Sentry from "@sentry/react";

export default function MessageCenter(props) {
    const { retrievalUrl, componentName } = props;

    return useMemo(
        () => (
            <section className={styles["message-center"]} id={componentName}>
                <div className={styles["message-center__inner"]}>
                    <MessageCenterContent retrievalUrl={retrievalUrl} componentName={componentName} />
                </div>
            </section>
        ),
        [componentName, retrievalUrl]
    );
}

const messageSliderSettings = {
    infinite: false,
    arrows: true,
    slidesToShow: 3,
    slidesToScroll: 3,
    initialSlide: 0,
    responsive: [
        {
            breakpoint: 1000,
            settings: {
                slidesToShow: 2,
                slidesToScroll: 2
            }
        },
        {
            breakpoint: 767,
            settings: {
                slidesToShow: 1,
                slidesToScroll:	1
            }
        }
    ]
};

const MessageSlider = (props) => {
    return (
        <Slider {...messageSliderSettings}>
            {props.messages ? props.messages.map(message => {
                return (
                    <Message message={message} type={message.Type} key={message.Type + message.DismissCallbackURL} />
                );
            }) : false}
        </Slider>
    );
}

const MessageCenterContent = ({retrievalUrl, componentName}) => {
    const queryClient = useQueryClient();

    //state
    const [showMessages, setShowMessages] = useState(false);
    const [messagesCount, setMessagesCount] = useState(0);

    //effects
    useEffect(() => {
        trace(`[MessageCenterContent] Update Message Count Effect ${messagesCount}`, performance.now(), () => { });
    }, [messagesCount]);

    useEmitterListener(componentName, USE_RETRIEVE_DATA_EVENTS.ON_FETCH_SUCCESS,
        (data) => {
            setMessagesCount(data.Messages?.length || 0);
            setShowMessages((data.Messages?.length || 0) > 0);
        }
    );

    useEffect(() => {
        const queryKey = [componentName, retrievalUrl];

        //define dismiss mutation
        queryClient.setMutationDefaults('dismissMessage', {
            mutationFn: callbackUrl => fetchHelper(callbackUrl, { method: 'POST' }),
            onMutate: async (callbackUrl) => {
                //cancel any pending dismiss
                await queryClient.cancelQueries(queryKey);
    
                //retrieve previous value
                const previousValue = queryClient.getQueryData(queryKey);
    
                //remove message from array of messages
                queryClient.setQueryData(queryKey, old => {
                    let filteredArray = old.Messages.filter(x => x.DismissCallbackURL !== callbackUrl);
                    setMessagesCount(filteredArray?.length || 0);
                    setShowMessages((filteredArray?.length || 0) > 0);
                    return {
                        Messages: filteredArray
                    };
                });
    
                //return previous value
                return previousValue;
            },
            onError: (error, variables, previousValue) => {
                Sentry.captureException(error);
                console.error('[Message] Error occured in Message.', error);
    
                //set previous value if error occured (reinstate message)
                queryClient.setQueryData(queryKey, previousValue);
            },
            onSettled: () => {
                //run MessageCenter query again, on error or success, just to be safe
                queryClient.invalidateQueries(queryKey);
            }
        });
    }, [queryClient, retrievalUrl, componentName]);

    //render
    return (
        <>
            {useMemo(
                () => (
                    <div className={styles["message-center__header"]}>
                        <h2 className={styles["message-center__header-title"]}>
                            Messages
                            <span className={styles["message-center__count"]}>
                                {messagesCount}
                            </span>
                        </h2>
                        <span className={styles["message-center__toggle"]} onClick={() => { setShowMessages(show => !show); }}>
                            {showMessages ? DisplayToggle.Hide : DisplayToggle.Show}
                            <IconAngleDownBold className={`${styles["message-center__toggle-icon"]} ${showMessages ? false : styles["message-center__toggle-icon-show"]}`} />
                        </span>
                    </div>
                ),
                [messagesCount, showMessages]
            )}
            {useMemo(
                () => (
                    <LoadingHandler retrievalUrl={retrievalUrl} componentName={componentName} queryOpts={{ staleTime: 0 }}>
                        {(data) => (
                            <>
                                {showMessages && <MessageCenterMessages data={data} />}
                            </>
                        )}
                    </LoadingHandler>
                ),
                [componentName, retrievalUrl, showMessages]
            )}
        </>
    );
};

const MessageCenterMessages = ({data}) => {
    const { Messages: messages } = data;
    
    return (
        <div className={styles["slider-wrap"]}>
            <MessageSlider messages={messages} />
        </div>
    );
};

const DisplayToggle = {
    Show:"Show",
    Hide:"Hide"
};
